import type {
    GroupChannel,
    GroupChannelListQueryParams,
} from '@sendbird/chat/groupChannel';
import GroupChannelList from '@sendbird/uikit-react/GroupChannelList';
import React, { useRef, useState } from 'react';
import { BodySmallEmphasized, View, XStack, YStack } from 'smoothie';

import { ChannelPreview } from './ChannelPreview';
import { GuideMessagingChannel } from './GuideMessagingChannel';

export const GuideMessagingWidget = () => {
    const [currentChannel, setCurrentChannel] = useState<GroupChannel | null>(
        null,
    );
    const hasPreventedChannelAutoSelect = useRef(false);

    // If channelListQuery object changes, ChannelList re-renders, so it is better to keep them in state/memoized.
    // See https://community.sendbird.com/t/includeempty-option-is-re-rendering-the-channellist/3315.
    const [channelListQuery] = useState<GroupChannelListQueryParams>({
        includeEmpty: true, // Query channels that include channels that have no messages. Channels have this state when a chat between a Guide and a Member is first created.
    });

    return (
        <>
            {sendbirdCssOverrides}
            <View width="100%" height="78vh" $lg={{ height: '72vh' }}>
                <XStack className="sendbird-app__wrap" gap="1vw">
                    <View className="sendbird-app__channellist-wrap">
                        <GroupChannelList
                            channelListQueryParams={channelListQuery}
                            selectedChannelUrl={currentChannel?.url}
                            onChannelSelect={(channel) => {
                                // When the component first loads, it will try to auto-select the first channel in the channel list, which leads to undesirable marked as read behavior.
                                // To prevent this from happening, once this callback fires for the first time, we prevent the setting of the current channel, but allow it to proceed on subsequent calls to this callback.
                                // See https://community.sendbird.com/t/setting-initial-channel/2291.
                                if (!hasPreventedChannelAutoSelect.current) {
                                    hasPreventedChannelAutoSelect.current = true;
                                    return;
                                }

                                setCurrentChannel(channel ?? null);
                            }}
                            renderHeader={() => (
                                <YStack
                                    height="100%"
                                    justifyContent="center"
                                    paddingHorizontal={8}
                                    paddingVertical={18}
                                >
                                    <BodySmallEmphasized>
                                        My Membership Channels
                                    </BodySmallEmphasized>
                                </YStack>
                            )}
                            renderChannelPreview={({ channel }) => (
                                <ChannelPreview channel={channel} />
                            )}
                            allowProfileEdit={false} // Determines whether to allow the user to edit their profile.
                            onChannelCreated={() => {}} // noop
                        />
                    </View>
                    <GuideMessagingChannel
                        sendbirdChannel={currentChannel}
                        shouldShowLinkToMembershipDetails={true}
                    />
                </XStack>
            </View>
        </>
    );
};

// Override styling of Sendbird UIKit ChannelList components.
const sendbirdCssOverrides = (
    <style type="text/css">
        {`
            /**
             * Top border on channel list not needed
             */
            .sendbird-app__channellist-wrap {
                border-top: none !important;
            }
            
            /**
             * Reset styles on channel list header to give our custom component more control of style
             */
            .sendbird-app__channellist-wrap .sendbird-channel-list .sendbird-channel-list__header {
                height: auto;
                min-height: unset;
                border-bottom: 1px solid var(--sendbird-light-onlight-04);
            }

            /**
             * Hides the Avatar component in the ChannelList ChannelPreview component.
             * We hide this because every Member will just default to a generic user icon.
             */
            .sendbird-channel-preview__avatar {
                display: none !important;
            }

            /**
             * Hides the badge indicating the number of Members in a channel in the ChannelList ChannelPreview component.
             * We hide this because our channels will only ever have two or three total participants, guide included, and ergo it is not valuable to display.
             */
            .sendbird-channel-preview__content__upper__header__total-members {
                display: none !important;
            }
            
            /**
             * Extend the width of elements in the ChannelList ChannelPreview component.
             * This is desirable with the removal of the ChannelPreview Avatar component.
             */
            .sendbird-channel-preview__content__upper {
                width: 260px !important;
            }
            .sendbird-channel-preview__content__lower {
                width: 260px !important;
            }
            .sendbird-channel-preview__content__upper__header__channel-name {
                max-width: 180px !important;
            }
            
            /**
             * When hovering over the ChannelList ChannelPreview, do not hide the last message timestamp or unread message count
             */
            .sendbird-channel-preview:hover
                .sendbird-channel-preview__content__upper__last-message-at {
                display: initial !important;
            }
            .sendbird-channel-preview:hover
                .sendbird-channel-preview__content__lower__unread-message-count {
                display: initial !important;
            }
            
            /**
             * Hides the Sendbird userId in the ChannelList Header.
             */
            .sendbird-channel-header__title__right__user-id {
                display: none !important;
            }
            
            /**
             * Vertically centers the Sendbird user's name (nickname) in the ChannelList Header.
             * Note: This is used in combination with hiding the userId.
             */
            .sendbird-channel-header__title__right {
                padding-top: 16px !important;
            }
            
            /**
             * Disables the gray background hover effect on the ChannelList Header.
             */
            .sendbird-channel-header__title:hover {
                background: transparent !important;
            }
            
            /**
             * Vertically center unread message count badge in the ChannelList ChannelPreview.
             */
            .sendbird-badge .sendbird-badge__text {
                padding-bottom: 2px;
            }
            
            /**
             * On large screens, on the Messaging page, we can make the messages wider, and fit more content horizontally and vertically.
             */
            @media (min-width: 1440px) {
                .sendbird-app__wrap .sendbird-message-content .sendbird-message-content__middle {
                    max-width: 600px;
                }
            }
        `}
    </style>
);
